import { loadMathJaxScript } from 'modules/assessment/loadMathJaxScript';
import React, { useMemo, useRef, useEffect } from 'react';

/**
 * Custom hook to load MathJax and trigger typesetting when content changes.
 */
const useMathJaxTypeset = (ref, dependencies = []) => {
    useEffect(() => {
        if (typeof window !== 'undefined') {
            loadMathJaxScript();
        }
    }, []); // Run once on component mount

    useEffect(() => {
        // Trigger typesetting whenever content or view state changes
        if (ref.current && window.MathJax && window.MathJax.Hub) {
            // Typeset the specific element and its children
            window.MathJax.Hub.Queue(["Typeset", window.MathJax.Hub, ref.current]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref, ...dependencies]);
};

/**
 * Wrapper component to hold content intended for MathJax rendering.
 */
const MathContent = React.memo(({ children, className, tag = 'p' }) => {
    const Tag = tag;
    return <Tag className={className}>{children}</Tag>;
});

// --- UTILITY FUNCTIONS ---
export const CUSTOM_STYLES = `
    /* The 3D styles are kept inline as they are necessary transformations */
    .transform-style-3d { transform-style: preserve-3d; }
    .backface-hidden { backface-visibility: hidden; }
    .rotate-y-180 { transform: rotateY(180deg); }
`;

// REMOVED applyThemeClass

// The logic inside these functions is now fixed to use 'dark:' classes
export const getDifficultyColorClass = (difficultyCode) => {
    switch (difficultyCode) {
        case 'B': return `bg-green-600 dark:bg-green-700 text-white`;
        case 'I': return `bg-yellow-500 dark:bg-yellow-600 text-gray-900 dark:text-white`;
        case 'A': return `bg-red-600 dark:bg-red-700 text-white`;
        default: return `bg-gray-400 dark:bg-gray-500 text-white`;
    }
};
const getDifficultyText = (difficultyCode) => {
    switch (difficultyCode) {
        case 'B': return 'Basic';
        case 'I': return 'Intermediate';
        case 'A': return 'Advanced';
        default: return 'Unknown';
    }
};
const getStudyMaterialIcon = (type) => {
    // The classes here are fixed to use 'dark:'
    const iconColor = 'text-gray-500 dark:text-gray-400';
    switch (type.toLowerCase()) {
        case 'video': return `<svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-red-500 dark:text-red-400" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" /></svg>`;
        case 'pdf': return `<svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-pink-500 dark:text-pink-400" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm6 0a1 1 0 100 2h.01a1 1 0 100-2H10zm0 4a1 1 0 100 2h.01a1 1 0 100-2H10zm0 4a1 1 0 100 2h.01a1 1 0:100-2H10z" clipRule="evenodd" /></svg>`;
        case 'image': return `<svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-purple-500 dark:text-purple-400" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 4-3 5 5z" clipRule="evenodd" /></svg>`;
        default: return `<svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 ${iconColor}" viewBox="0 0 20 20" fill="currentColor"><path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" /><path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h.01a1 1 0 100-2H10zm3 0a1 1 0 000 2h.01a1 1 0 100-2H13zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H10z" clipRule="evenodd" /></svg>`;
    }
};
export const getCompletionPercentage = (cu, completedFacts) => {
    const total = cu?.intuitionFacts.length || 0;
    if (total === 0) return 0;
    const completed = cu.intuitionFacts.filter(fact => completedFacts[cu.code]?.[fact.id]).length;
    return Math.round((completed / total) * 100);
};

// The logic inside these functions is now fixed to use 'dark:' classes
const getSummaryHeatmapColorClass = (percentage) => {
    if (percentage === 100) return `bg-amber-600 dark:bg-amber-700 text-white shadow-2xl shadow-amber-800/70 dark:shadow-amber-400/30 border-amber-800 dark:border-amber-600`;
    if (percentage > 75) return `bg-ingido-400 dark:bg-ingido-600 text-white dark:text-gray-100 border-ingido-600 dark:border-ingido-500`;
    if (percentage > 50) return `bg-yellow-300 dark:bg-yellow-700 text-gray-800 dark:text-gray-100 border-yellow-500 dark:border-yellow-600`;
    if (percentage > 25) return `bg-lime-200 dark:bg-lime-800 text-gray-800 dark:text-gray-200 border-lime-400 dark:border-lime-700`;
    return `bg-lime-100 dark:bg-lime-900 text-gray-800 dark:text-gray-200 border-lime-300 dark:border-lime-800`;
};

// The logic inside these functions is now fixed to use 'dark:' classes
const getHeatmapColorClass = (percentage) => {
    if (percentage === 100) {
        return `bg-gray-300 dark:bg-gray-400 text-gray-700 dark:text-gray-900 border-gray-400 shadow-sm`;
    }
    if (percentage > 75) {
        return `bg-gray-200 dark:bg-gray-300 text-gray-600 dark:text-gray-800 border-gray-300`;
    }
    if (percentage > 50) {
        return `bg-gray-100 dark:bg-gray-200 text-gray-500 dark:text-gray-700 border-gray-200`;
    }
    if (percentage > 25) {
        return `bg-gray-50 dark:bg-gray-100 text-gray-400 dark:text-gray-600 border-gray-100`;
    }
    return `bg-white dark:bg-gray-50 text-gray-300 dark:text-gray-500 border-gray-100`;
};

export const findUnitByCode = (code, conceptUnits) => {
    return conceptUnits.find(cu => cu.code === code);
};


// --- CONCEPT DETAIL VIEW ---
// isDarkMode flag is removed from the component's props
export const ConceptDetailView = React.memo(({ cuCode, conceptUnits, UI_CONFIG, completionStatus, onBack, onOpenModal, getUnitCompletion }) => {

    const detailRef = useRef(null); // Ref for the main container
    const selectedCU = useMemo(() => findUnitByCode(cuCode, conceptUnits), [cuCode, conceptUnits]);
    const percentage = getUnitCompletion(cuCode);

    // Trigger typesetting when selectedCU changes
    useMathJaxTypeset(detailRef, [selectedCU]);

    if (!selectedCU) return null;

    // [Class definitions remain the same]
    // isDarkMode flag is removed from utility function calls
    const heatmapClass = getSummaryHeatmapColorClass(percentage);
    const factStyles = UI_CONFIG.cardStyles.ifCard;

    // Direct dark: classes applied to strings that previously used applyThemeClass
    const unitBorderColor = 'border-gray-300 dark:border-gray-600';
    const headerTextColor = percentage === 100 ? 'text-white' : 'text-gray-600 dark:text-gray-300';
    const materialTextColor = percentage === 100 ? 'text-gray-100' : 'text-gray-600 dark:text-gray-400';
    const materialHeaderColor = percentage === 100 ? 'text-white' : 'text-gray-700 dark:text-gray-300';
    const noMaterialTextColor = percentage === 100 ? 'text-gray-300' : 'text-gray-500 dark:text-gray-400';

    return (
        <div className="p-4" id="if-list-view" ref={detailRef}> {/* Attach ref here */}
            {/* Back button - dark: class already present */}
            <button onClick={onBack} id="back-to-list-button" className="flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition mb-6 font-semibold">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                {UI_CONFIG.buttonText.backToList}
            </button>

            <div className="flex flex-col lg:flex-row gap-8">
                {/* --- LEFT COLUMN (Unit Header) --- */}
                <div className="lg:w-1/4 order-1 lg:order-1">
                    <div id="unit-header" className={`rounded-xl p-6 shadow-xl ${heatmapClass} transition-all duration-500`}>
                        {/* 1. MATH CONTENT ENABLED: Unit Title */}
                        <MathContent tag="h2" className="text-2xl font-black capitalize">{selectedCU.name}</MathContent>

                        <p id="unit-info" className={`text-sm opacity-90 mt-1 ${headerTextColor}`}>
                            Difficulty: <span className="font-bold">{getDifficultyText(selectedCU.difficulty)}</span> | Class Level: <span className="font-bold">{selectedCU.classLevel}</span>
                        </p>

                        <p id="unit-status" className="text-lg opacity-90 mt-2 font-semibold">
                            {UI_CONFIG.flower.name} <p>Status: {percentage}% Complete</p>
                            {percentage === 100 && <span className="ml-2 font-bold">{UI_CONFIG.viewTitles.statusComplete}</span>}
                        </p>
                        <div id="unit-materials-container" className={`mt-4 pt-3 border-t ${unitBorderColor}`}>
                            <p id="materials-header" className={`font-semibold text-sm mb-2 ${materialHeaderColor}`}>{UI_CONFIG.viewTitles.materialsHeader}</p>
                            <ul id="unit-materials-list" className="space-y-1 text-sm">
                                {selectedCU.studyMaterials && selectedCU.studyMaterials.length > 0 ? (
                                    selectedCU.studyMaterials.map((mat, index) => (
                                        // isDarkMode flag removed from getStudyMaterialIcon
                                        <li key={index} className={`flex items-center ${materialTextColor}`}>
                                            <span dangerouslySetInnerHTML={{ __html: getStudyMaterialIcon(mat.type) }} />
                                            {/* 2. MATH CONTENT ENABLED: Material Name */}
                                            <a href={mat.link} target="_blank" rel="noopener noreferrer" className="ml-2 underline hover:text-indigo-600 dark:hover:text-indigo-300">
                                                <MathContent tag="span" className="">{mat.name}</MathContent> ({mat.type.toUpperCase()})
                                            </a>
                                        </li>
                                    ))
                                ) : (
                                    <li className={`italic ${noMaterialTextColor}`}>No supplemental resources found for this unit.</li>
                                )}
                            </ul>
                        </div>
                    </div>
                </div>

                {/* --- RIGHT COLUMN (Fact Grid) --- */}
                <div className="lg:w-3/4 order-2 lg:order-2">
                    <div id="unit-header" className={`rounded-xl p-2 ${heatmapClass} transition-all duration-500`}>
                        {/* 1. MATH CONTENT ENABLED: Unit Title */}
                        <MathContent tag="p">{selectedCU.description}</MathContent>
                    </div>
                    <h3 id="detail-instruction-header" className={`text-xl py-4 md:text-2xl font-bold text-gray-700 dark:text-gray-200 mb-6`}>{UI_CONFIG.viewTitles.detailInstruction}</h3>
                    <div id="fact-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {selectedCU.intuitionFacts?.sort((a, b) => a.order - b.order).map(fact => {
                            const isStudied = completionStatus[cuCode]?.[fact.id];

                            // Direct dark: classes applied to strings that previously used applyThemeClass
                            const innerContainerClasses = `${factStyles.innerContainer.split('||')[0]} dark:${factStyles.innerContainer.split('||')[1]} ${factStyles.hover}`;
                            const completionBorder = isStudied ? UI_CONFIG.flower.colorComplete.split('||')[0] + ' dark:' + UI_CONFIG.flower.colorComplete.split('||')[1] : 'border-gray-200 dark:border-gray-700';

                            const icon = isStudied ? UI_CONFIG.flower.iconComplete : UI_CONFIG.flower.iconIncomplete;
                            // isDarkMode flag removed from getDifficultyColorClass
                            const difficultyClass = getDifficultyColorClass(fact.difficulty.charAt(0));
                            const titleClass = factStyles.titleFont.split('||')[0] + ' dark:' + factStyles.titleFont.split('||')[1];

                            return (
                                <div key={fact.id} className={`${factStyles.base}`} onClick={() => onOpenModal(fact)}>
                                    <div className={`${innerContainerClasses} ${completionBorder}`}>
                                        <div className="flex justify-between items-start mb-3">
                                            <span className={`${factStyles.difficultyTag} ${difficultyClass}`}>{fact.difficulty}</span>
                                            <span className={`${factStyles.iconSize}`}>{icon}</span>
                                        </div>
                                        {/* 3. MATH CONTENT ENABLED: Fact Title (Substring) */}
                                        <MathContent tag="h4" className='text-sm text-gray-600 font-medium leading-relaxed 
                           line-clamp-2 group-hover:line-clamp-none transition-all duration-300 ease-in-out'>{fact.description?.substring(0, 50)}...</MathContent>
                                        {/* dark:text-gray-400 already present */}
                                        <p className={`text-xs text-gray-500 dark:text-gray-400 mt-2`}>Questions: {fact.assessments.length} | Level: {fact.classLevel}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
});


// --- CONCEPT UNIT LIST VIEW ---
// isDarkMode flag is removed from the component's props
export const ConceptUnitListView = React.memo(({ conceptUnits, UI_CONFIG, getUnitCompletion, onSelectCU, onBack }) => {

    const listRef = useRef(null); // Ref for the main container
    const styles = UI_CONFIG.cardStyles.cuCard;

    // Trigger typesetting when the list of units changes
    useMathJaxTypeset(listRef, [conceptUnits]);

    return (
        <div className="min-h-screen  p-2 sm:p-4">
            <div className="mx-auto max-w-8xl pl-4">
                <div id="cu-list-view" ref={listRef}> {/* Attach ref here */}
                    <header className="mb-6 flex flex-col items-start justify-between sm:flex-row sm:items-center">
                        <div>
                            {/* Removed text-gray-600||... class logic as no dark theme class was present */}
                            <p className="mt-1 text-gray-600">
                                Browse and search your active concepts.
                            </p>
                        </div>
                        {/* Search Bar */}
                        <div className="mt-4 w-full sm:mt-0 sm:w-auto">
                            {/* dark: classes already present */}
                            <button
                                onClick={onBack}
                                id="back-to-list-button"
                                className="flex items-center font-semibold text-indigo-600 transition hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                <span className="mr-2">Back</span>
                            </button>
                        </div>
                    </header>


                    <div id="cu-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {conceptUnits?.sort((a, b) => a.order - b.order).map(cu => {
                            const percentage = getUnitCompletion(cu.code);
                            // isDarkMode flag removed from getHeatmapColorClass
                            const heatmapClass = getHeatmapColorClass(percentage);

                            const statusLabel = percentage === 100 ? UI_CONFIG.leaf.statusComplete : UI_CONFIG.leaf.name.toUpperCase().split(' ')[0];

                            // Direct dark: classes applied to strings that previously used applyThemeClass
                            const statusColor = percentage === 100
                                ? UI_CONFIG.leaf.colorComplete.split('||')[0] + ' dark:' + UI_CONFIG.leaf.colorComplete.split('||')[1]
                                : UI_CONFIG.leaf.colorIncomplete.split('||')[0] + ' dark:' + UI_CONFIG.leaf.colorIncomplete.split('||')[1];

                            const icon = percentage === 100 ? UI_CONFIG.leaf.iconComplete : UI_CONFIG.leaf.icon;

                            const difficultyText = getDifficultyText(cu.difficulty);
                            const statusPillClasses = `${styles.statusPill} ${statusColor}`;
                            const cardTextColor = 'text-gray-800 dark:text-gray-100';
                            const progressTextColor = 'text-gray-700 dark:text-gray-300';
                            const innerContainerBase = styles.innerContainer.split('||')[0] + ' dark:' + styles.innerContainer.split('||')[1];
                            const cuCardBase = `${styles.base} ${styles.hover}`;


                            return (
                                <div
                                    key={cu.code}
                                    id={`${cu.code}-card`}
                                    className={cuCardBase}
                                    onClick={() => onSelectCU(cu.code)}
                                >
                                    <div className={`${innerContainerBase} ${heatmapClass} flex flex-col h-full relative`}>


                                        {/* 2. Body Content */}
                                        <div className="flex-grow flex flex-col gap-1">
                                            {/* Title: Darkest Gray */}
                                            <MathContent tag="h3" className={`${styles.headerFont} ${cardTextColor} text-xl font-bold text-gray-900 leading-tight group-hover:underline decoration-gray-400 underline-offset-4 transition-all`}>
                                                {cu.name}
                                            </MathContent>

                                            {/* Description: 
            - line-clamp-2: Cuts off after 2 lines with '...'
            - group-hover:line-clamp-none: Expands to full text on hover
            - transition-all: Animates the height change 
        */}
                                            <div className="relative mt-1">
                                                <MathContent
                                                    tag="p"
                                                    className="text-sm text-gray-600 font-medium leading-relaxed 
                           line-clamp-2 group-hover:line-clamp-none transition-all duration-300 ease-in-out"
                                                >
                                                    {cu.description}
                                                </MathContent>
                                            </div>

                                            {/* Progress Section */}
                                            <div className={`mt-4 pt-3 border-t border-gray-200 flex items-center justify-between ${progressTextColor}`}>
                                                {/* Badge: Now using Dark Gray bg for high contrast 'Dark Mode' feel on the element */}
                                                <span className={`${styles.tagBase} ${getDifficultyColorClass(cu.difficulty)} bg-gray-800 text-gray-100 px-3 py-1 rounded text-[10px] font-bold uppercase tracking-wider shadow-sm`}>
                                                    {difficultyText}
                                                </span>
                                                <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Progress</span>
                                                <span className="text-sm font-extrabold text-gray-800">{percentage}%</span>
                                            </div>
                                        </div>

                                        {/* 3. Footer: Status Pill */}
                                        {/* Styled as a solid Dark Gray block for the 'Dark Gray Colors' requirement */}
                                        <div className={`${statusPillClasses} mt-3 text-center text-xs font-bold py-2 px-4 rounded bg-gray-900 text-white shadow-sm border border-gray-900`}>
                                            {statusLabel}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div >
    );
});